// External Dependencies
import { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { Analytics } from '@vercel/analytics/react';
import './App.css';

// Components
import Layout from './components/Layout';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import SearchPage from './pages/study-spots/SearchPage';
import AddStudySpot from './pages/study-spots/AddStudySpot';
import ListingPage from './pages/study-spots/ListingPage';
import ClassroomsByBuilding from './pages/available-classrooms/ClassroomsByBuilding';
import RoomDetails from './pages/available-classrooms/RoomDetails';

import ClassroomFinder from './pages/available-classrooms/ClassroomFinder';
import ReportBug from './pages/ReportBug';
import Privacy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsofService';

// Context Providers
import { ThemeProvider } from './contexts/ThemeContext';

// Constants
const SESSION_KEY = 'userSession';
const GOOGLE_ANALYTICS_ID = 'G-7JYJGVNHRH';

// Theme Configuration
const theme = extendTheme({
  breakpoints: {
    base: '0em',
    sm: '30em',
    md: '48em',
    lg: '62em',
    xl: '80em',
    '2xl': '96em',
  }
});

// Utility Components
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function GoogleAnalyticsTracker() {
  const location = useLocation();
  
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', GOOGLE_ANALYTICS_ID, {
        page_path: location.pathname,
      });
    }
  }, [location]);
  
  return null;
}

// Analytics Setup
function initGoogleAnalytics() {
  const script1 = document.createElement('script');
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
  script1.async = true;
  document.head.appendChild(script1);
  
  const script2 = document.createElement('script');
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${GOOGLE_ANALYTICS_ID}');
  `;
  document.head.appendChild(script2);
}

// Main App Component
function App() {
  useEffect(() => {
    // Initialize Analytics
    initGoogleAnalytics();
    
    // Session Management
    const lastSession = localStorage.getItem(SESSION_KEY);
    const currentSession = Date.now();
    
    if (!lastSession || currentSession - parseInt(lastSession, 10) > 3600000) {
      localStorage.removeItem('selectedTimeRange');
      localStorage.setItem(SESSION_KEY, currentSession.toString());
    }
  }, []);
  
  return (
    <ChakraProvider theme={theme}>
      <ThemeProvider>
        <ScrollToTop />
        <GoogleAnalyticsTracker />
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="/search-page" element={<SearchPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/add-study-spot" element={<AddStudySpot />} />
            <Route path="/listing/:id" element={<ListingPage />} />
            <Route path="/availability" element={<ClassroomFinder />} />
            <Route path="/building/:building" element={<ClassroomsByBuilding />} />
            <Route path="/room/:building/:room" element={<RoomDetails />} />
            <Route path="/report" element={<ReportBug />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<TermsOfService />} />
          </Route>
        </Routes>
        <Analytics />
      </ThemeProvider>
    </ChakraProvider>
  );
}

export default App;